import { EnumHelper } from "@/Common/Helpers/EnumHelper";
import { SitesModel } from "@/Common/Models/Sites/SitesModel";
import { CanadaState } from "@/Common/Resources/Lookups/Enums/CommonLookupCodes";

class EnvironmentTax {
    public static hasHst(): boolean {
        return this.getInstance().hasHst;
    }

    public static hasGst(): boolean {
        return this.getInstance().hasGst;
    }

    public static hasQst(): boolean {
        return this.getInstance().hasQst;
    }

    public static hasPst(): boolean {
        return this.getInstance().hasPst;
    }

    public static hasPstOrQst(): boolean {
        return this.hasPst() || this.hasQst();
    }

    public static hasGenericTax(): boolean {
        return this.getInstance().hasSitesOutsideCanada;
    }

    public static getPstOrQstLabel(): string {
        const label = [];
        if (this.hasPst())
            label.push(cacheStore.GetString("PstMLabel"));

        if (this.hasQst())
            label.push(cacheStore.GetString("QstMLabel"));

        return label.join("/") || cacheStore.GetString("PstQstMLabel");
    }

    public static isHstProvinceByLookupCode(lookupCode: string): boolean {
        if (!EnumHelper.isDefined(CanadaState, lookupCode))
            return false;

        return this.isHstProvince(lookupCode as CanadaState);
    }

    public static isHstProvince(province: CanadaState): boolean {
        return this.getInstance().HstProvinces.any(p => p === province);
    }

    public static isGstProvinceByLookupCode(lookupCode: string): boolean {
        if (!EnumHelper.isDefined(CanadaState, lookupCode))
            return false;

        return this.isGstProvince(lookupCode as CanadaState);
    }

    public static isGstProvince(province: CanadaState): boolean {
        return this.getInstance().GstProvinces.any(p => p === province);
    }

    public static isPstProvinceByLookupCode(lookupCode: string): boolean {
        if (!EnumHelper.isDefined(CanadaState, lookupCode))
            return false;

        return this.isPstProvince(lookupCode as CanadaState);
    }

    public static isPstProvince(province: CanadaState): boolean {
        return this.getInstance().PstProvinces.any(p => p === province);
    }

    public static isQstProvinceByLookupCode(lookupCode: string): boolean {
        if (!EnumHelper.isDefined(CanadaState, lookupCode))
            return false;

        return this.isQstProvince(lookupCode as CanadaState);
    }

    public static isQstProvince(province: CanadaState): boolean {
        return this.getInstance().QstProvinces.any(p => p === province);
    }

    public static isPstOrQstProvinceByLookupCode(lookupCode: string): boolean {
        return this.isPstProvinceByLookupCode(lookupCode)
            || this.isQstProvinceByLookupCode(lookupCode);
    }

    public static isPstOrQstProvince(province: CanadaState): boolean {
        return this.isPstProvince(province)
            || this.isQstProvince(province);
    }

    private static _instance: EnvironmentTax = null;
    public static getInstance(): EnvironmentTax {
        if (this._instance !== null)
            return this._instance;

        this._instance = new EnvironmentTax(cacheStore.SiteCache);
        return this._instance;
    }

    public readonly hasHst: boolean;
    public readonly hasGst: boolean;
    public readonly hasPst: boolean;
    public readonly hasQst: boolean;
    public readonly hasSitesOutsideCanada: boolean;

    public readonly HstProvinces: CanadaState[] = [
        CanadaState.Ontario,
        CanadaState.NewBrunswick,
        CanadaState.Newfoundland,
        CanadaState.NovaScotia,
        CanadaState.PrinceEdwardIsland
    ];

    public readonly GstProvinces: CanadaState[] = [
        CanadaState.Nunavut,
        CanadaState.NorthwestTerritories,
        CanadaState.Yukon,
        CanadaState.Alberta,
        CanadaState.Manitoba,
        CanadaState.Quebec,
        CanadaState.Saskatchewan,
        CanadaState.BritishColumbia
    ];

    public readonly PstProvinces: CanadaState[] = [
        CanadaState.Manitoba,
        CanadaState.Saskatchewan,
        CanadaState.BritishColumbia
    ];

    public readonly QstProvinces: CanadaState[] = [
        CanadaState.Quebec
    ];

    private readonly sites: SitesModel[];

    private constructor(sites: SitesModel[]) {
        this.sites = sites;
        this.hasHst = this.HstProvinces.some(province => this.isProvinceInSiteCache(province));
        this.hasGst = this.GstProvinces.some(province => this.isProvinceInSiteCache(province));
        this.hasPst = this.PstProvinces.some(province => this.isProvinceInSiteCache(province));
        this.hasQst = this.QstProvinces.some(province => this.isProvinceInSiteCache(province));
        this.hasSitesOutsideCanada = sites.some(site => !site.isCanadaSite());
    }

    private isProvinceInSiteCache(province: CanadaState): boolean {
        return this.sites
            .some(site => site.isPreferredSiteAddressInProvince(province));
    }
}

export { EnvironmentTax };
